<template>
  <div class="row audio-record">
    <!-- Record button with countdown -->
    <div class="col-12 text-centered" v-if="!recordedAudioUrl">
      <button
        class="record-button"
        :class="{ recording: isRecording, counting: countdown !== null }"
        @click="toggleRecording"
        :disabled="countdown !== null"
      >
        <!-- Initializing -->
        <i class="bxr bx-loader-dots loading" v-if="countdown == -1"></i>
        <!-- Show countdown number inside button when counting down -->
        <span v-else-if="countdown !== null" class="countdown-number">{{ countdown }}</span>
        <!-- Show microphone or stop icon when not counting down -->
        <i
          v-else
          class="bxr"
          :class="{ 'bxs-microphone-alt': !isRecording, 'bx-stop': isRecording }"
        ></i>
      </button>
    </div>
    <template v-else>
      <!-- Accept buttons -->
      <div class="col-3">
        <button @click="uploadAudio" :disabled="uploading">
          <i class="bxr bxs-check-circle green icon"></i>
        </button>
        <button @click="resetRecording">
          <i class="bxr bxs-x-circle red icon"></i>
        </button>
      </div>
      <!-- Audio waveform display -->
      <div class="col-9">
        <AudioWaveform :audioUrl="recordedAudioUrl" v-if="recordedAudioUrl" />
      </div>
    </template>
    <div v-if="error" class="alert alert-danger col-12">
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onBeforeUnmount, type Ref, onMounted } from 'vue'
import AudioWaveform from '@/components/common/AudioWaveform.vue'
import { api } from '@/libs/apiLocal'
import { MediaRecorder, register, type IMediaRecorder } from 'extendable-media-recorder'
import { connect } from 'extendable-media-recorder-wav-encoder'

const props = defineProps<{
  engine: tAudioEngine
  entityId: number | string
}>()

const emit = defineEmits<{
  (e: 'uploaded', id: number): void
  (e: 'error', error: string | null): void
}>()

// Recording state
const isRecording = ref(false)
const recordedAudioUrl = ref('')
const error = ref('')
const uploading = ref(false)
const countdown: Ref<number | null> = ref(null)
// MediaRecorder values
let initialized = false
let mediaRecorder: IMediaRecorder | null = null
let recordingChunks: Blob[] = []
let countdownInterval: number | null = null
let recordedAudio: Blob | null = null
let stream: MediaStream | null = null
const removeLastMs = 100 // how many milliseconds to remove from the end of the recording. should be divisible by 4.

// Toggle recording with countdown
const toggleRecording = () => {
  if (isRecording.value) {
    stopRecording()
  } else {
    startCountdown()
  }
}

async function initializeMediaRecorder() {
  if (initialized) return // Already initialized
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    await register(await connect())
    // wait additional 1 sec to ensure stream is ready
    await new Promise((resolve) => setTimeout(resolve, 1000))
    initialized = true
  } catch (err) {
    console.error('Error initializing MediaRecorder:', err)
  }
}

// Start the 3-2-1 countdown
async function startCountdown() {
  if (countdown.value !== null) return // Don't start if already counting down
  if (!initialized) {
    countdown.value = -1 // Indicate initializing state
    await initializeMediaRecorder()
  }
  // register media recorder
  try {
    // await 100ms
    mediaRecorder = new MediaRecorder(stream!, { mimeType: 'audio/wav' })
    // add event listeners
    mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data.size > 0) {
        recordingChunks.push(e.data)
      }
    }
    mediaRecorder.onstop = () => {
      // remove last 4 chunks
      recordingChunks = recordingChunks.slice(0, -4)
      recordedAudio = new Blob(recordingChunks, { type: 'audio/wav' })
      recordedAudioUrl.value = URL.createObjectURL(recordedAudio)
      isRecording.value = false
    }

    countdown.value = 3 // Start countdown from 3
    countdownInterval = window.setInterval(async () => {
      countdown.value!--
      if (countdown.value === 0) {
        if (countdownInterval) {
          clearInterval(countdownInterval)
          countdownInterval = null
        }
        // alow human to react
        countdown.value = null
        isRecording.value = true
        await new Promise((resolve) => setTimeout(resolve, 100))
        startRecording()
      }
    }, 500)
  } catch (err) {
    console.error('Error accessing microphone:', err)
    error.value = 'Could not access microphone. Please check permissions.'
    isRecording.value = false
    countdown.value = 0
  }
}

// Audio recording functions
const startRecording = async () => {
  if (mediaRecorder) {
    error.value = ''
    recordingChunks = []
    isRecording.value = true
    mediaRecorder.start(removeLastMs / 4)
  }
}

const stopRecording = () => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop()
    mediaRecorder = null
  }
}

const resetRecording = () => {
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value)
  }
  recordedAudio = null
  recordedAudioUrl.value = ''
  error.value = ''
}

// Upload function
async function uploadAudio() {
  if (!recordedAudio) {
    error.value = 'No audio recorded to upload.'
    emit('error', error.value)
    return
  }

  uploading.value = true
  emit('error', null)

  try {
    const formData = new FormData()
    formData.append('file', recordedAudio, 'recording.wav')

    const response = await api.post(`/audio/${props.engine}/${props.entityId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    // Emit the uploaded event with the response data
    emit('uploaded', response.data)

    // Reset the recording state after successful upload
    resetRecording()
  } catch (err) {
    emit('error', String(err))
  } finally {
    uploading.value = false
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.code === 'Space' && !recordedAudioUrl.value) {
    event.preventDefault() // Prevent default spacebar action
    toggleRecording() // Start recording on spacebar press
  }
}

onMounted(() => {
  // add event listener for keyboard spacebar
  window.addEventListener('keydown', handleKeyDown)
})

// Clean up resources when component is destroyed
onBeforeUnmount(() => {
  if (isRecording.value) {
    stopRecording()
  }

  window.removeEventListener('keydown', handleKeyDown)

  if (stream) {
    stream!.getTracks().forEach((track) => track.stop())
  }

  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value)
  }

  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})
</script>
